import React, { useState } from 'react';
import { 
  Container, Box, Typography, TextField, Button, CircularProgress, Alert, Paper,
  FormControl, InputLabel, Select, MenuItem, FormHelperText 
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import useProjectStore from '../../store/useProjectStore';
import useForm from '../../hooks/useForm';

const ProjectUpload = () => {
  const navigate = useNavigate();
  const [apiError, setApiError] = useState('');
  const [success, setSuccess] = useState(false);
  const { createProject, creatingProject } = useProjectStore();

  const validate = (values) => {
    const errors = {};
    if (!values.title) errors.title = 'Title is required';
    if (!values.description) errors.description = 'Description is required';
    if (!values.techStack) errors.techStack = 'Tech Stack is required';
    if (!values.githubRepoUrl) {
      errors.githubRepoUrl = 'GitHub URL is required';
    } else {
      const githubRegex = /^https?:\/\/(www\.)?github\.com\/[\w-]+\/[\w.-]+(\/)?$/;
      if (!githubRegex.test(values.githubRepoUrl)) {
        errors.githubRepoUrl = 'Please enter a valid GitHub repository URL';
      }
    }
    return errors;
  };

  const submitProject = async (values) => {
    setApiError('');
    setSuccess(false);

    try {
      await createProject(values);
      setSuccess(true);
      setTimeout(() => {
        navigate('/student/dashboard');
      }, 1500);
    } catch (err) {
      console.error('Upload error:', err);
      setApiError(err.response?.data?.message || 'Failed to upload project. Please try again.');
      // Re-throw to let useForm know it failed (if we wanted to use useForm's error handling, but we are handling it here)
    }
  };

  const {
    values,
    errors,
    isSubmitting,
    handleChange,
    handleSubmit
  } = useForm({
    title: '',
    description: '',
    githubRepoUrl: '',
    techStack: ''
  }, validate);

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Upload Project
          </Typography>
          <Typography variant="body1" color="textSecondary" align="center" paragraph>
            Share your work with faculty for evaluation.
          </Typography>

          {apiError && <Alert severity="error" sx={{ mb: 3 }}>{apiError}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 3 }}>Project uploaded successfully! Redirecting...</Alert>}

          <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(submitProject); }} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="title"
              label="Project Title"
              name="title"
              value={values.title}
              onChange={handleChange}
              disabled={isSubmitting || success || creatingProject}
              error={!!errors.title}
              helperText={errors.title}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              id="description"
              label="Description"
              name="description"
              multiline
              rows={4}
              value={values.description}
              onChange={handleChange}
              disabled={isSubmitting || success || creatingProject}
              error={!!errors.description}
              helperText={errors.description}
            />

            <FormControl 
              fullWidth 
              margin="normal" 
              required 
              error={!!errors.techStack} 
              disabled={isSubmitting || success || creatingProject}
            >
              <InputLabel id="tech-stack-label">Tech Stack</InputLabel>
              <Select
                labelId="tech-stack-label"
                id="techStack"
                name="techStack"
                value={values.techStack}
                label="Tech Stack"
                onChange={handleChange}
              >
                <MenuItem value="MEAN">MEAN</MenuItem>
                <MenuItem value="MERN">MERN</MenuItem>
              </Select>
              {errors.techStack && <FormHelperText>{errors.techStack}</FormHelperText>}
            </FormControl>

            <TextField
              margin="normal"
              required
              fullWidth
              id="githubRepoUrl"
              label="GitHub Repository URL"
              name="githubRepoUrl"
              placeholder="https://github.com/username/repository"
              value={values.githubRepoUrl}
              onChange={handleChange}
              disabled={isSubmitting || success}
              error={!!errors.githubRepoUrl}
              helperText={errors.githubRepoUrl || "Must be a valid public GitHub repository URL"}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              startIcon={(isSubmitting || creatingProject) ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
              disabled={isSubmitting || success || creatingProject}
              sx={{ mt: 3, mb: 2 }}
            >
              {(isSubmitting || creatingProject) ? 'Uploading...' : 'Submit Project'}
            </Button>
            
            <Button
              fullWidth
              variant="text"
              onClick={() => navigate('/student/dashboard')}
              disabled={isSubmitting || success || creatingProject}
            >
              Cancel
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ProjectUpload;
